import React, { useState } from "react";
import MastersLayout from "../MastersLayout";
import { useDefaultBrandingOnly } from "../../../../contexts/AssociateCompanyContext";
import { GetAuditScheduleBreadcrumb } from "./Companies.constants";
import { useExportAuditSchedule, useGetCompanies, useGetCompanyLocations, useImportAuditSchedule } from "../../../../backend/masters";
import { validatorTypes } from "@data-driven-forms/react-form-renderer";
import FormRenderer, { ComponentMapper, FormTemplate, componentTypes } from "../../../common/FormRenderer";
import { Button } from "react-bootstrap";
import AuditScheduleImportModal from "./AuditScheduleImportModal";
import PageLoader from "../../../shared/PageLoader";
import { getMaxMonthYear, getMinMonthYear, readBlobAsJson } from "../../../../utils/common";
import { DEFAULT_OPTIONS_PAYLOAD } from "../../../common/Table";
import dayjs from "dayjs";
import { ActivityType } from "../Master.constants";
import { API_DELIMITER, ERROR_MESSAGES } from "../../../../utils/constants";
import { EXCEL_FILE_REGEX, FILE_SIZE } from "../../../common/Constants";
import BaseImportModal from "../../../common/BaseImportModal";
import { AxiosError } from "axios";
import { toast } from "react-toastify";

const DEFAULT_EXPORT_DATA = {
    hideButtons: true,
    month: new Date(),
    types: ['Display', 'Registers', 'Returns'].map(x => {
        return { value: x, label: x }
    })
}

function AuditSchedule(this: any) {
    // Super Admin page, not scoped to one client — white-label branding never
    // applies here regardless of any Company/Associate Company filter below.
    useDefaultBrandingOnly();
    const [form, setForm] = useState<any>({});
    const [importFile, setImportFile] = useState(false);
    const [breadcrumb] = useState(GetAuditScheduleBreadcrumb('Location Audit Schedule'));
    const [exportData, setExportData] = useState<any>({ ...DEFAULT_EXPORT_DATA });
    const [parentCompany, setParentCompany] = useState<any>(null);
    const [associate_company, setAssociateCompany] = useState<any>(null);
    const { companies, isFetching: fetchingCompanies } = useGetCompanies({ ...DEFAULT_OPTIONS_PAYLOAD, filters: { is_parent_eq:'true', is_active_eq: true } });
    const { companies: associateCompanies, isFetching: fetchingAssociateCompanies } = useGetCompanies({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: { is_parent_eq: 'false', is_active_eq: true, parent_company_id_eq: (parentCompany || {}).value }
    }, Boolean(parentCompany));
    const { locations, isFetching: fetchingLocations } = useGetCompanyLocations({
        ...DEFAULT_OPTIONS_PAYLOAD, filters: { company_id_eq: (associate_company || {}).value },
        sort: ['location_name asc'],
        include: { location: ['state', 'city'] },
        includes: { location: ['state', 'city'] }
    }, Boolean(associate_company));
    const { exportAuditSchedule, exporting } = useExportAuditSchedule(async (response: any) => {
        if(response instanceof AxiosError){
            const contentType = response.response && response.response.headers['content-type'];
            if (contentType.includes('application/json')) {
                const errorJson =  await readBlobAsJson((response.response || {}).data || {});
                let errors = (errorJson || {}).errors|| [ERROR_MESSAGES.DEFAULT];
                toast.error(errors.join(', '));
            }
        } else {
            const blob = new Blob([response.data], { type: response.headers['content-type'] })
            const URL = window.URL || window.webkitURL;
            const downloadUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = getFileName();
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    }, () => {

    });

    const schema = {
        fields: [
            {
                component: componentTypes.SELECT,
                name: 'parentCompany',
                label: 'Company',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                options: companies,
                isLoading: fetchingCompanies,
                onChange: onParentCompanyChange.bind(this),
                className: 'grid-col-100'
            },
            {
                component: componentTypes.SELECT,
                name: 'associate_company',
                label: 'Associate Company',
                options: associateCompanies,
                isDisabled: !Boolean(parentCompany),
                isLoading: fetchingAssociateCompanies,
                onChange: onAssociateCompanyChange.bind(this),
                className: 'grid-col-100'
            },
            {
                component: componentTypes.SELECT,
                name: 'locations',
                label: 'Locations',
                options: (locations || []).map((x: { location: {}; }) => {
                    const { id, name, code, state, city }: any = x.location || {};
                    return {
                        id,
                        name: `${name} (${state.code}-${city.code}-${code})`,
                        code: `${state.code}-${city.code}-${code}`
                    }
                }),
                isDisabled: !Boolean(associate_company),
                isLoading: fetchingLocations,
                className: 'grid-col-100'
            },
            {
                component: componentTypes.SELECT,
                name: 'types',
                label: 'Activity Types',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                className: 'grid-col-100',
                options: ActivityType,
                isMulti: true
            },
            {
                component: componentTypes.MONTH_PICKER,
                name: 'month',
                label: 'Month',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                className: 'grid-col-100',
                initialValue: exportData.month,
                minDate: getMinMonthYear(),
                maxDate: getMaxMonthYear(),
                range: true
            }
        ]
    };

    function getFileName() {
        const { parentCompany, associate_company, locations } = exportData;
        const _d = { ...parentCompany, ...associate_company, ...locations };
        const result = [
            _d.parentCompany.code
        ];
        if (_d.associate_company) {
            result.push(_d.associate_company.code);
        }
        if (_d.locations) {
            result.push(_d.locations.code);
        }
        return `${result.join('-')}.xlsx`
    }

    function debugForm(_form: any[]) {
        setForm(_form);
        setExportData(_form.values);
    }

    function onParentCompanyChange(e: any) {
        setParentCompany(e);
        setAssociateCompany(null);
        setExportData({ ...exportData, parentCompany: e, associate_company: null, locations: null });
    }

    function onAssociateCompanyChange(e: any) {
        setAssociateCompany(e);
        setExportData({ ...exportData, associate_company: e, locations: null });
    }

    function handleSubmit() {
        if (form.valid) {
            const { parentCompany, associate_company, locations, month, types } = exportData;
            let fromDate, toDate;
            if (Array.isArray(month)) {
                fromDate = new Date(month[0]);
                fromDate.setDate(1);
                toDate = new Date(month[1] || month[0]);
                toDate.setDate(1);
            } else {
                fromDate = new Date(month);
                fromDate.setDate(1);
                toDate = new Date(month);
                toDate.setDate(1);
            }
            const payload = {
                company_id: parentCompany.value,
                associate_company_id: (associate_company || {}).value || '',
                location_id: (locations || {}).value || '',
                from_date: dayjs(new Date(fromDate)).local().format(),
                to_date: dayjs(new Date(toDate)).local().format(),
                types: types.map((x: { value: any; }) => x.value).join(API_DELIMITER)
            };
            exportAuditSchedule(payload);
        }
    }

    function resetForm() {
        setExportData({ ...DEFAULT_EXPORT_DATA });
    }

    const importSchema = {
        fields: [
            {
                component: componentTypes.FILE_UPLOAD,
                label: 'Upload File',
                name: 'file',
                type: 'file',
                validate: [
                    { type: validatorTypes.REQUIRED },
                    { type: 'file-type', regex: EXCEL_FILE_REGEX },
                    { type: 'file-size', maxSize: 10 * FILE_SIZE.MB }
                ]
            },
        ],
    };

    return (
        <>
            <MastersLayout title="Location Audit Schedule" breadcrumbs={breadcrumb}>
                <div className="d-flex flex-column mx-0 mt-4">
                    <div className="d-flex flex-row justify-content-center mb-4">
                        <div className="w-50 px-4 border-end" style={{ maxWidth: '550px' }}>
                            <div className="d-flex flex-column card shadow p-4">
                                <div className="text-lg fw-bold">Export Audit Schedule</div>
                                <div className="d-flex flex-column h-100 justify-space-between py-2 horizontal-form col2">
                                    <FormRenderer FormTemplate={FormTemplate}
                                        initialValues={exportData}
                                        componentMapper={ComponentMapper}
                                        schema={schema}
                                        debug={debugForm}
                                    />
                                </div>
                                <div className="d-flex justify-content-end mt-4 me-4">
                                    <Button variant="outline-secondary" className="btn btn-outline-secondary px-4" onClick={resetForm}>{'Reset'}</Button>
                                    <Button variant="primary" onClick={handleSubmit} className="ms-4 px-4" disabled={!form.valid}>{'Export'}</Button>
                                </div>
                            </div>
                        </div>
                        <div className="w-100 px-4" style={{ maxWidth: "calc(100% - 550px)", minWidth: "50%" }}>
                            <div className="d-flex flex-column">
                                <div className="d-flex flex-column card shadow p-4 mb-4">
                                    <div className="text-lg fw-bold mb-4">Import Audit Schedule</div>
                                    <ul>
                                        <li>Click on Import Button</li>
                                        <li>Select the file and upload</li>
                                        <li>If there are errors, click on the hyper link and the application will export the errors in excel file for you to edit and re-upload</li>
                                    </ul>
                                    <div>
                                        <Button variant="primary" onClick={() => setImportFile(true)} className="px-4">{'Import'}</Button>
                                    </div>
                                </div>
                                <div className="d-flex flex-column card shadow p-4">
                                    <div className="text-lg fw-bold mb-4">Instructions for Export</div>
                                    <ul>
                                        <li>Select Company</li>
                                        <li>Select Associate Company (Optional)</li>
                                        <li>Select Location (Optional)</li>
                                        <li>Please note that if you are not selecting the Associate Company or Location, then the application will export all data for all Associate Companies or Locations.</li>
                                        <li>Select Month & Year. Here you can select the range of months to create the excel export</li>
                                        <li>Click Export button to export the data into an excel sheet.</li>
                                    </ul>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </MastersLayout>
            {/* {
                importFile && <AuditScheduleImportModal onClose={() => setImportFile(false)} />
            } */}

            {
                importFile && <BaseImportModal onClose={() => setImportFile(false)} importSchema={importSchema} importFunction={useImportAuditSchedule} title="Import Audit Schedule" />
            }

            {
                exporting && <PageLoader>Generating Audit Schedule...</PageLoader>
            }
        </>
    )
}

export default AuditSchedule;